// The runtime wire shapes (BackendSurvey, BackendResults, etc.) live as
// private types inside front/src/surveys/api.ts, so we duplicate just the
// bits we need here rather than coupling preview-mode to that module's
// parser. (The typings disconnect note for asPreviewHandler lives in
// handlers/index.ts.)

import { getPreviewGraph } from "../graph";
import type {
  PreviewSurvey,
  PreviewSurveyAggregate,
  PreviewSurveyImpression,
  PreviewSurveyQuestion,
  PreviewSurveyAnswer,
} from "../graph/types";
import { PREVIEW_ENV_ID } from "../runtime";

const DAY_MS = 24 * 60 * 60 * 1000;

interface BackendQuestion {
  id: string;
  surveyId: string;
  position: number;
  type: PreviewSurveyQuestion["type"];
  text: string;
  options:
    | { type: "single_choice"; choices: string[] }
    | { type: "open_text"; placeholder: string }
    | { type: "rating"; lowLabel: string; highLabel: string }
    | { type: "yes_no" }
    | { type: "nps" };
  createdAt: string;
  updatedAt: string;
}

interface BackendTrigger {
  mode: "page" | "event";
  matchType: string;
  patterns: string[];
  eventNames: string[];
}

interface BackendSurvey {
  id: string;
  envId: string;
  name: string;
  description: string;
  status: PreviewSurvey["status"];
  version: number;
  trigger: BackendTrigger;
  idleTimeSeconds: number;
  startDate: string | null;
  endDate: string | null;
  appearance: PreviewSurvey["appearance"];
  questions: BackendQuestion[];
  responseCount: number;
  createdAt: string;
  updatedAt: string;
  sourceConversationId: string | null;
}

function toBackendQuestion(
  surveyId: string,
  q: PreviewSurveyQuestion,
  isoTimestamps: { createdAt: string; updatedAt: string },
): BackendQuestion {
  const base = {
    id: q.id,
    surveyId,
    position: q.position,
    text: q.text,
    createdAt: isoTimestamps.createdAt,
    updatedAt: isoTimestamps.updatedAt,
  };
  switch (q.type) {
    case "single_choice":
      return {
        ...base,
        type: "single_choice",
        options: {
          type: "single_choice",
          choices: q.choices.map((c) => c.label),
        },
      };
    case "open_text":
      return {
        ...base,
        type: "open_text",
        options: { type: "open_text", placeholder: q.placeholder },
      };
    case "yes_no":
      return { ...base, type: "yes_no", options: { type: "yes_no" } };
    case "rating":
      return {
        ...base,
        type: "rating",
        options: {
          type: "rating",
          lowLabel: q.lowLabel,
          highLabel: q.highLabel,
        },
      };
    case "nps":
      return { ...base, type: "nps", options: { type: "nps" } };
  }
}

function toBackendSurvey(
  s: PreviewSurvey,
  agg: PreviewSurveyAggregate | undefined,
): BackendSurvey {
  const createdAtIso = new Date(s.createdAt).toISOString();
  const updatedAtIso = new Date(s.updatedAt).toISOString();
  return {
    id: s.id,
    envId: PREVIEW_ENV_ID,
    name: s.name,
    description: s.description,
    status: s.status,
    version: 1,
    trigger: {
      mode: s.trigger.mode,
      matchType: s.trigger.matchType,
      patterns: [...s.trigger.patterns],
      eventNames: [...s.trigger.eventNames],
    },
    idleTimeSeconds: s.idleTimeSeconds,
    startDate: s.startDate ? new Date(s.startDate).toISOString() : null,
    endDate: s.endDate ? new Date(s.endDate).toISOString() : null,
    appearance: s.appearance,
    questions: s.questions.map((q) =>
      toBackendQuestion(s.id, q, {
        createdAt: createdAtIso,
        updatedAt: updatedAtIso,
      }),
    ),
    responseCount:
      agg?.impressions.filter((i) => i.completedAt !== null).length ?? 0,
    createdAt: createdAtIso,
    updatedAt: updatedAtIso,
    sourceConversationId: null,
  };
}

interface DateRangeMs {
  fromMs: number;
  toMs: number;
}

function parseDateRange(params: {
  from?: string;
  to?: string;
}): DateRangeMs | null {
  if (!params.from || !params.to) return null;
  const fromMs = Date.parse(params.from);
  const toMs = Date.parse(params.to);
  if (Number.isNaN(fromMs) || Number.isNaN(toMs)) return null;
  return { fromMs, toMs };
}

function withinRange(
  impression: PreviewSurveyImpression,
  range: DateRangeMs | null,
): boolean {
  if (!range) return true;
  return impression.shownAt >= range.fromMs && impression.shownAt <= range.toMs;
}

// --- Handlers --------------------------------------------------------------

export function handleGetSurveys(_params: Record<string, never>) {
  const graph = getPreviewGraph();
  return {
    surveys: graph.surveys.map((s) =>
      toBackendSurvey(s, graph.surveyAggregates.get(s.id)),
    ),
  };
}

export function handleGetSurvey(params: { surveyId: string }) {
  const graph = getPreviewGraph();
  const survey = graph.surveys.find((s) => s.id === params.surveyId);
  if (!survey) return { survey: null };
  return {
    survey: toBackendSurvey(
      survey,
      graph.surveyAggregates.get(params.surveyId),
    ),
  };
}

interface DistributionEntryWire {
  label: string;
  count: number;
}

interface NpsGroupingWire {
  totalRespondents: number;
  pctRespondents: number | null;
}

interface QuestionSummaryWire {
  questionId: string;
  questionIndex: number;
  questionType: PreviewSurveyQuestion["type"];
  questionPrompt: string;
  totalResponses: number;
  data: unknown;
}

function summarizeAnswers(
  question: PreviewSurveyQuestion,
  answers: PreviewSurveyAnswer[],
  index: number,
): QuestionSummaryWire {
  const total = answers.length;
  const base = {
    questionId: question.id,
    questionIndex: index,
    questionType: question.type,
    questionPrompt: question.text,
    totalResponses: total,
  };

  switch (question.type) {
    case "single_choice": {
      const counts = new Map<string, number>(
        question.choices.map((c) => [c.label, 0]),
      );
      for (const a of answers)
        counts.set(a.value, (counts.get(a.value) ?? 0) + 1);
      const distribution: DistributionEntryWire[] = question.choices.map(
        (c) => ({ label: c.label, count: counts.get(c.label) ?? 0 }),
      );
      return { ...base, data: { distribution } };
    }
    case "yes_no": {
      let yes = 0;
      let no = 0;
      for (const a of answers) {
        if (a.value === "Yes") yes++;
        else if (a.value === "No") no++;
      }
      return {
        ...base,
        data: {
          distribution: [
            { label: "Yes", count: yes },
            { label: "No", count: no },
          ],
        },
      };
    }
    case "rating": {
      const counts = [0, 0, 0, 0, 0];
      let scoreSum = 0;
      for (const a of answers) {
        const n = parseInt(a.value, 10);
        if (n >= 1 && n <= 5) {
          counts[n - 1]++;
          scoreSum += n;
        }
      }
      const score = total === 0 ? 0 : scoreSum / total;
      const distribution: DistributionEntryWire[] = counts.map((count, i) => ({
        label: String(i + 1),
        count,
      }));
      return { ...base, data: { score, distribution } };
    }
    case "nps": {
      const counts = new Array(11).fill(0);
      let promoters = 0;
      let passives = 0;
      let detractors = 0;
      for (const a of answers) {
        const n = parseInt(a.value, 10);
        if (n < 0 || n > 10 || Number.isNaN(n)) continue;
        counts[n]++;
        if (n >= 9) promoters++;
        else if (n >= 7) passives++;
        else detractors++;
      }
      const score =
        total === 0
          ? 0
          : Math.round(((promoters - detractors) / total) * 10000) / 100;
      const grouping = (n: number): NpsGroupingWire => ({
        totalRespondents: n,
        pctRespondents: total === 0 ? null : (n / total) * 100,
      });
      const distribution: DistributionEntryWire[] = counts.map((count, i) => ({
        label: String(i),
        count,
      }));
      return {
        ...base,
        data: {
          score,
          promoters: grouping(promoters),
          demoters: grouping(detractors),
          passives: grouping(passives),
          distribution,
        },
      };
    }
    case "open_text": {
      // Cap samples so the UI doesn't get flooded.
      const samples = answers.slice(0, 10).map((a) => a.value);
      return { ...base, data: { samples } };
    }
  }
}

export function handleGetSurveyResults(params: {
  surveyId: string;
  from?: string;
  to?: string;
}) {
  const graph = getPreviewGraph();
  const survey = graph.surveys.find((s) => s.id === params.surveyId);
  const agg = graph.surveyAggregates.get(params.surveyId);
  if (!survey || !agg) {
    return {
      results: {
        surveyId: params.surveyId,
        impressions: 0,
        responses: 0,
        responseRate: null,
        questionSummaries: [],
      },
    };
  }
  const range = parseDateRange(params);
  const filtered = agg.impressions.filter((i) => withinRange(i, range));
  const responseImpressions = filtered.filter((i) => i.completedAt !== null);

  // Bucket all answers by questionId once so per-question summaries are
  // O(answers) rather than O(impressions × questions²) via .find().
  const answersByQuestion = new Map<string, PreviewSurveyAnswer[]>();
  for (const imp of responseImpressions) {
    for (const a of imp.answers) {
      let bucket = answersByQuestion.get(a.questionId);
      if (!bucket) {
        bucket = [];
        answersByQuestion.set(a.questionId, bucket);
      }
      bucket.push(a);
    }
  }

  const summaries = survey.questions.map((q, i) =>
    summarizeAnswers(q, answersByQuestion.get(q.id) ?? [], i),
  );

  return {
    results: {
      surveyId: params.surveyId,
      impressions: filtered.length,
      responses: responseImpressions.length,
      responseRate:
        filtered.length === 0
          ? null
          : responseImpressions.length / filtered.length,
      questionSummaries: summaries,
    },
  };
}

interface TimeseriesPointWire {
  bucket: string;
  impressions: number;
  responses: number;
}

export function handleGetSurveyTimeseries(params: {
  surveyId: string;
  from?: string;
  to?: string;
}) {
  const graph = getPreviewGraph();
  const agg = graph.surveyAggregates.get(params.surveyId);
  const range = parseDateRange(params) ?? {
    fromMs: graph.windowStart,
    toMs: graph.windowEnd,
  };

  // Daily granularity for the preview window. Anchor buckets to UTC midnight
  // so the wire shape mirrors what the real backend returns.
  const startOfDay = (ms: number) => Math.floor(ms / DAY_MS) * DAY_MS;
  const firstBucket = startOfDay(range.fromMs);
  const lastBucket = startOfDay(range.toMs);
  const points: TimeseriesPointWire[] = [];

  if (!agg) {
    return { granularity: "day" as const, points };
  }

  const byDay = new Map<number, { impressions: number; responses: number }>();
  for (const imp of agg.impressions) {
    if (imp.shownAt < range.fromMs || imp.shownAt > range.toMs) continue;
    const dayKey = startOfDay(imp.shownAt);
    const entry = byDay.get(dayKey) ?? { impressions: 0, responses: 0 };
    entry.impressions++;
    if (imp.completedAt !== null) entry.responses++;
    byDay.set(dayKey, entry);
  }

  for (let d = firstBucket; d <= lastBucket; d += DAY_MS) {
    const entry = byDay.get(d) ?? { impressions: 0, responses: 0 };
    points.push({
      bucket: new Date(d).toISOString(),
      impressions: entry.impressions,
      responses: entry.responses,
    });
  }

  return { granularity: "day" as const, points };
}

interface ResponseRowWire {
  deviceId: string;
  userId: string | null;
  startedAt: string;
  completedAt: string | null;
  pageUrl: string | null;
  answers: Record<string, string>;
}

export function handleGetSurveyResponses(params: {
  surveyId: string;
  limit?: number;
  offset?: number;
  from?: string;
  to?: string;
}) {
  const graph = getPreviewGraph();
  const survey = graph.surveys.find((s) => s.id === params.surveyId);
  const agg = graph.surveyAggregates.get(params.surveyId);
  if (!survey || !agg) {
    return { page: { rows: [], total: 0 } };
  }
  const range = parseDateRange(params);
  const responses = agg.impressions
    .filter((i) => i.completedAt !== null && withinRange(i, range))
    // Newest first, matching the typical UI expectation.
    .sort((a, b) => b.shownAt - a.shownAt);

  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  const slice = responses.slice(offset, offset + limit);

  const rows: ResponseRowWire[] = slice.map((imp) => {
    // Backend keys answers by question index (string), not question id, so
    // build a per-impression {questionId → value} map and read by question.
    const valueByQuestionId = new Map<string, string>();
    for (const a of imp.answers) valueByQuestionId.set(a.questionId, a.value);
    const answers: Record<string, string> = {};
    survey.questions.forEach((q, i) => {
      const v = valueByQuestionId.get(q.id);
      if (v !== undefined) answers[String(i)] = v;
    });
    return {
      deviceId: imp.deviceID,
      userId: imp.identifier,
      startedAt: new Date(imp.shownAt).toISOString(),
      completedAt:
        imp.completedAt === null
          ? null
          : new Date(imp.completedAt).toISOString(),
      pageUrl: imp.pageUrl,
      answers,
    };
  });

  return { page: { rows, total: responses.length } };
}
